import { KeyboardEvent } from "react";
import { ListboxContextProps } from "./Listbox";

export const scrollToOption = (node: HTMLElement | undefined) => {
  node?.scrollIntoView({
    block: "nearest",
    inline: "center",
  });
};

export const onListboxKeyDown = (
  e: KeyboardEvent<HTMLUListElement>,
  context: ListboxContextProps<any>,
) => {
  const {
    options,
    currentFocus,
    selectionMode,
    onSelectionChange,
    selected,
    setSelected,
    items,
    controlled,
  } = context;

  if (!options) return;

  const currentNode = Array.from(options).find(
    (node) => node.id === currentFocus,
  ) as HTMLElement;

  const currentIndex = Array.from(options).indexOf(currentNode);

  switch (e.code) {
    case "ArrowDown":
      e.preventDefault();
      if (currentIndex === options.length - 1) {
        goTo(options[0], 0, context);
      } else {
        goTo(options[currentIndex + 1], currentIndex + 1, context);
      }
      break;
    case "ArrowUp":
      e.preventDefault();
      if (currentIndex === 0) {
        goTo(options[options.length - 1], options.length - 1, context);
      } else {
        goTo(options[currentIndex - 1], currentIndex - 1, context);
      }
      break;
    case "Home":
      e.preventDefault();
      goTo(options[0], 0, context);
      break;
    case "End":
      e.preventDefault();
      goTo(options[options.length - 1], options.length - 1, context);
      break;

    case "Enter":
    case "Space":
      e.preventDefault();

      if (!currentFocus) return;
      if (controlled) return;

      if (selectionMode === "single") {
        if (
          typeof selected === "object"
            ? selected.label === currentFocus
            : selected === currentFocus
        ) {
          setSelected?.(undefined);
          onSelectionChange?.(undefined);
          return;
        }

        if (items) {
          const enabledItems = items.filter((item) => !(item as any)?.disabled);
          setSelected?.(enabledItems[currentIndex]);
          onSelectionChange?.(enabledItems[currentIndex]);
          return;
        }

        if (!items) {
          setSelected?.(currentFocus);
          onSelectionChange?.(currentFocus);
          return;
        }
      }

      if (selectionMode === "multiple") {
        const selectedItems = Array.isArray(selected) ? selected : [selected];

        if (items) {
          const focusedItem = items.find(
            (item) => (item as any).label === currentFocus,
          );

          if (selectedItems.includes(focusedItem)) {
            const selectedItemsWithItemRemoved = selectedItems.filter(
              (selectedItem) => selectedItem !== focusedItem,
            );
            setSelected?.(selectedItemsWithItemRemoved);
            onSelectionChange?.(selectedItemsWithItemRemoved);
            return;
          } else {
            const selectedItemsWithItemAdded = [...selectedItems, focusedItem];

            setSelected?.(selectedItemsWithItemAdded);
            onSelectionChange?.(selectedItemsWithItemAdded);
            return;
          }
        }

        if (!items) {
          if (selectedItems.includes(currentFocus)) {
            setSelected?.(
              selectedItems.filter((item) => item !== currentFocus),
            );
            onSelectionChange?.(
              selectedItems.filter((item) => item !== currentFocus),
            );
            return;
          }
          setSelected?.([...selectedItems, currentFocus]);
          onSelectionChange?.([...selectedItems, currentFocus]);
        }
      }
      break;

    default:
      break;
  }
};

const goTo = (
  option: HTMLElement,
  index: number,
  context: ListboxContextProps<any>,
) => {
  const {
    setCurrentFocus,
    setSelected,
    selectionMode,
    disableAutoSelectOnFocus,
    onSelectionChange,
    items,
    controlled,
  } = context;

  scrollToOption(option);
  if (disableAutoSelectOnFocus || selectionMode === "multiple") {
    setCurrentFocus(option?.id);
    return;
  }

  setCurrentFocus(option?.id);

  if (controlled) return;

  if (items) {
    const enabledItems = items.filter((item) => !(item as any)?.disabled);
    setSelected(enabledItems[index]);
    onSelectionChange?.(enabledItems[index]);
  } else {
    setSelected(option?.id);
    onSelectionChange?.(option?.id);
  }
};
